import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { countParam, type LabelListDownload } from '@/pages/catalogImport';
import { getNodeTestIdProp } from '@/utils';
import { BarcodeIssuesBar } from './BarcodeIssuesBar';
import { BarcodeIssuesEmpty } from './BarcodeIssuesEmpty';
import { BarcodeIssuesLoadError } from './BarcodeIssuesLoadError';
import { BarcodeIssuesRefusalAlert } from './BarcodeIssuesRefusalAlert';
import { BarcodeIssuesSuccessAlert } from './BarcodeIssuesSuccessAlert';
import { BarcodeIssuesTable } from './BarcodeIssuesTable';
import type { UseBarcodeIssuesResult } from './useBarcodeIssues';

type BarcodeIssuesViewProps = {
    state: UseBarcodeIssuesResult;
    download: LabelListDownload;
};

const COPY = 'products.barcodeIssues';

/**
 * The Barcode issues tab (board `bc-Repair`, AC-09, AC-10), by the list's
 * status (rulebook §12): the title with the count once one is known, why
 * these barcodes are listed, the success or refusal alert, then placeholder
 * rows under the real headers while the first page loads, an inline error
 * with Retry when it could not be read, the empty state when nothing is
 * listed, or the table, with a notice above it when a refresh failed. While
 * rows are selected, the sticky bar.
 */
export const BarcodeIssuesView: React.FC<BarcodeIssuesViewProps> = ({
    state,
    download,
}) => {
    const { t } = useTranslation('pages');
    const { list, bar } = state;
    const isRefreshing =
        (list.status === 'ready' || list.status === 'empty') &&
        list.refresh === 'pending';

    return (
        <section
            aria-busy={isRefreshing || list.status === 'loading'}
            className="flex flex-col gap-4"
            {...getNodeTestIdProp('barcode-issues')}
        >
            <div className="flex flex-col gap-1">
                <Typography
                    type="h4"
                    {...getNodeTestIdProp('barcode-issues-title')}
                >
                    {list.status === 'ready'
                        ? t(`${COPY}.title`, {
                              count: list.totalCount,
                              total: countParam(list.totalCount),
                          })
                        : t(`${COPY}.tab`)}
                </Typography>
                <Typography color="muted" type="body-sm">
                    {t(`${COPY}.intro`)}
                </Typography>
                <Typography color="muted" type="body-xs">
                    {t(`${COPY}.keepHelp`)}
                </Typography>
            </div>

            {state.success === null ? null : (
                <BarcodeIssuesSuccessAlert
                    download={download}
                    success={state.success}
                />
            )}
            {state.refusal === null ? null : (
                <BarcodeIssuesRefusalAlert
                    onReload={state.onReload}
                    refusal={state.refusal}
                />
            )}

            {list.status === 'error' ? (
                <BarcodeIssuesLoadError kind="initial" onRetry={list.onRetry} />
            ) : null}
            {(list.status === 'ready' || list.status === 'empty') &&
            list.refresh === 'error' ? (
                <BarcodeIssuesLoadError kind="refresh" onRetry={list.onRetry} />
            ) : null}
            {list.status === 'empty' ? <BarcodeIssuesEmpty /> : null}
            {list.status === 'loading' ? (
                <BarcodeIssuesTable
                    isPlaceholder={false}
                    rows={null}
                    state={state}
                    totalCount={0}
                />
            ) : null}
            {list.status === 'ready' ? (
                <BarcodeIssuesTable
                    isPlaceholder={list.isPlaceholder}
                    rows={list.data}
                    state={state}
                    totalCount={list.totalCount}
                />
            ) : null}

            {bar.selectedCount > 0 ? (
                <BarcodeIssuesBar bar={bar} onSubmit={state.onSubmit} />
            ) : null}
        </section>
    );
};
